import { Fragment, useMemo, useState } from 'react';
import clsx from 'clsx';
import { CalendarDays, ChevronRight } from 'lucide-react';
import { Card, CardHeader, InlineStat } from '../../../components/ui/Card';
import { DotBadge } from '../../../components/ui/Badge';
import { InfoTip } from '../../../components/ui/InfoTip';
import { isConstant, Nil, Table, TableFooter, THead, Th, Td, Tr } from '../../../components/ui/Table';
import { Pagination, usePagination } from '../../../components/ui/Pagination';
import { EmptyState, ErrorState, LoadingState } from '../../../components/ui/States';
import { useCsmRoster, useCsmSnapshotHistory } from '../../../hooks/useDashboard';
import type { CsmSnapshotByDay } from '../../../api/dashboard';
import { formatDayMonthTime, formatDayMonthYear, formatTime } from '../../../utils/dateFormat';
import { bareDomain, companyLabel } from '../../Sales/shared';
import { formatCompact, formatNumber } from '../../../utils/numberFormat';
import { shiftDaysISO, todayISO } from './period';
import { mergeSnapshotRows } from './snapshotRows';

/** "2026-10-08" → "8 Oct 2026". */
function isoDay(iso: string | null): string {
  return iso ? formatDayMonthYear(`${iso.slice(0, 10)}T00:00:00`) : '';
}

const STATUS_LABEL: Record<string, string> = { active: 'Active', inactive: 'Inactive', unmapped: 'No users' };

/** SEQUESTO's own workspaces (sequesto.com / sequesto.io): never a customer row. */
function isInternalWorkspace(domain: string | null | undefined): boolean {
  return /(^|\.)sequesto\.(com|io)$/i.test(bareDomain(domain ?? ''));
}

const STATUS_DOT: Record<string, 'active' | 'warning' | 'inactive'> = {
  active: 'active',
  inactive: 'warning',
  unmapped: 'inactive',
};

/**
 * A date field that always reads "8 Oct 2026", whatever the browser locale. The
 * native picker sits invisibly on top, so clicking still opens it.
 */
function DateField({
  value,
  min,
  max,
  onChange,
  label,
}: {
  value: string;
  min?: string;
  max?: string;
  onChange: (value: string) => void;
  label: string;
}) {
  return (
    <label className="relative inline-flex items-center gap-1.5 rounded border border-border-light bg-surface-primary px-3 py-1.5 text-body-xs text-text-primary focus-within:border-text-primary">
      <CalendarDays className="h-3.5 w-3.5 text-text-tertiary" aria-hidden />
      <span className="tabular-nums">{isoDay(value)}</span>
      <input
        type="date"
        value={value}
        min={min}
        max={max}
        onChange={(e) => e.target.value && onChange(e.target.value)}
        aria-label={label}
        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
      />
    </label>
  );
}

/** One night's per-company snapshot, loaded when the day is opened. 25 rows a page. */
function SnapshotDay({ date }: { date: string }) {
  const day = useCsmSnapshotHistory({ date });
  // Named the way the CSM list names them ("ISS Facility Services UK", not "Issworld").
  const roster = useCsmRoster(true);
  const rosterById = useMemo(() => {
    const m = new Map<string, { name: string | null; domain: string | null }>();
    for (const r of roster.data?.rows ?? []) for (const id of r.matchedProdCompanyIds) m.set(id, { name: r.companyName, domain: r.domain });
    return m;
  }, [roster.data]);
  const all = useMemo(() => day.data?.companyRows ?? [], [day.data]);
  // SEQUESTO's own workspace is not a customer: left out, and said so in the footer.
  const external = useMemo(() => all.filter((c) => !isInternalWorkspace(c.domain)), [all]);
  const internal = all.length - external.length;
  // One row per company: test workspaces (awesomecompany.com) are dropped and two workspaces of one company
  // (the CSM list entry, else the domain) are summed.
  const rows = useMemo(
    () => mergeSnapshotRows(external, (c) => rosterById.get(c.companyId)?.name ?? (bareDomain(c.domain) || c.companyId)),
    [external, rosterById],
  );
  const testOrMerged = external.length - rows.length;
  const showStatus = !isConstant(rows, (c) => c.status);
  const { pageItems, ...paging } = usePagination(rows);
  if (day.isLoading) return <LoadingState />;
  if (day.error) return <ErrorState title="Couldn’t load this day" error={day.error} onRetry={() => day.refetch()} compact />;
  if (rows.length === 0) return <EmptyState title={`No company rows captured on ${isoDay(date)}`} compact />;
  return (
    <>
      <Table minWidth={760}>
        <THead>
          <tr>
            <Th>Company</Th>
            {showStatus && <Th>Status</Th>}
            <Th align="right">Members</Th>
            <Th align="right">Active</Th>
            <Th align="right">Events</Th>
            <Th align="right">Tokens</Th>
            <Th align="right">Last active</Th>
          </tr>
        </THead>
        <tbody>
          {pageItems.map((c) => (
            <Tr key={c.companyId}>
              <Td>
                <span className="font-medium text-text-primary">{rosterById.get(c.companyId)?.name ?? (companyLabel(c.domain) || c.companyId)}</span>
                {/* One domain rule: the CSM list's domain when the company is on it, else the platform's. */}
                {bareDomain(rosterById.get(c.companyId)?.domain ?? c.domain) && (
                  <span className="block text-body-xs text-text-tertiary">{bareDomain(rosterById.get(c.companyId)?.domain ?? c.domain)}</span>
                )}
              </Td>
              {showStatus && (
                <Td className="whitespace-nowrap">
                  <DotBadge status={STATUS_DOT[c.status] ?? 'inactive'}>{STATUS_LABEL[c.status] ?? c.status}</DotBadge>
                </Td>
              )}
              <Td align="right" className="tabular-nums">{formatNumber(c.memberCount)}</Td>
              <Td align="right" className="tabular-nums">
                <span className="block">{formatNumber(c.activeUsers30d)}</span>
                <span className="block text-body-xs text-text-tertiary">{Math.round(c.activeRate30d)}%</span>
              </Td>
              <Td align="right" className="tabular-nums">{formatNumber(c.loginCount30d)}</Td>
              <Td align="right" className="tabular-nums" title={formatNumber(c.totalTokens30d)}>
                {formatCompact(c.totalTokens30d) || '0'}
              </Td>
              <Td align="right" className="whitespace-nowrap text-text-secondary">
                {c.lastLoginAt ? formatDayMonthTime(c.lastLoginAt) : <Nil />}
              </Td>
            </Tr>
          ))}
        </tbody>
      </Table>
      <Pagination {...paging} onPage={paging.setPage} />
      <TableFooter>
        {formatNumber(rows.length)} companies captured on {isoDay(date)} · 30-day figures
        {internal > 0 && ` · SEQUESTO's own workspace left out`}
        {testOrMerged > 0 && ` · ${testOrMerged} test or duplicate ${testOrMerged === 1 ? 'workspace' : 'workspaces'} left out or merged`}
        {!showStatus && rows[0] && ` · all ${STATUS_LABEL[rows[0].status]?.toLowerCase() ?? rows[0].status}`}
      </TableFooter>
    </>
  );
}

/** Consecutive nights that stored the same company and person counts, newest first. */
interface Run {
  key: string;
  days: CsmSnapshotByDay[];
}

function collapseRuns(byDay: CsmSnapshotByDay[]): Run[] {
  const runs: Run[] = [];
  for (const day of byDay) {
    const last = runs[runs.length - 1];
    const prev = last?.days[last.days.length - 1];
    if (prev && prev.companies === day.companies && prev.persons === day.persons) last!.days.push(day);
    else runs.push({ key: day.snapshotDate, days: [day] });
  }
  return runs;
}

/** "02:00–02:04": the spread of first-write times across a folded run of nights. */
function writtenRange(days: CsmSnapshotByDay[]): string | null {
  const times = days.map((d) => d.firstWrittenAt).filter((t): t is string => !!t).map((t) => formatTime(t)).sort();
  if (times.length === 0) return null;
  const lo = times[0]!;
  const hi = times[times.length - 1]!;
  return lo === hi ? lo : `${lo}–${hi}`;
}

/** CSM › Snapshot history (admin): is the nightly archive being written? */
export function SnapshotsTab() {
  const [from, setFrom] = useState(() => shiftDaysISO(31));
  const [to, setTo] = useState(todayISO);
  const [openDate, setOpenDate] = useState<string | null>(null);
  const [openRuns, setOpenRuns] = useState<Set<string>>(() => new Set());
  const history = useCsmSnapshotHistory({ from, to });
  const health = history.data?.health;
  const byDay = useMemo(() => history.data?.byDay ?? [], [history.data]);
  const runs = useMemo(() => collapseRuns(byDay), [byDay]);
  // The same company count every night is said once, not on every row.
  const companiesSame = isConstant(byDay, (d) => d.companies);
  const cols = companiesSame ? 4 : 5;
  const colWidths = companiesSame ? ['1.5rem', 'auto', '7rem', '9rem'] : ['1.5rem', 'auto', '7rem', '7rem', '9rem'];
  // The archive covers platform companies with a domain set in the product, not the CS list (18 vs 28 customers on 9 Oct 2026).
  const companiesNote =
    companiesSame && byDay[0]
      ? `${formatNumber(byDay[0].companies)} companies every night: the platform companies with a domain set in the product, not the CS list`
      : null;

  const toggleRun = (key: string) =>
    setOpenRuns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const dayRow = (row: CsmSnapshotByDay, nested = false) => {
    const isOpen = openDate === row.snapshotDate;
    return (
      <Fragment key={row.snapshotDate}>
        <Tr onClick={() => setOpenDate(isOpen ? null : row.snapshotDate)}>
          <Td className="pr-2">
            <ChevronRight
              className={clsx('h-4 w-4 text-text-muted transition-transform', isOpen && 'rotate-90', nested && 'ml-4')}
              aria-hidden
            />
          </Td>
          <Td className={clsx('font-medium text-text-primary', nested && 'pl-4')}>{isoDay(row.snapshotDate)}</Td>
          {!companiesSame && <Td align="right" className="tabular-nums">{formatNumber(row.companies)}</Td>}
          <Td align="right" className="tabular-nums">{formatNumber(row.persons)}</Td>
          <Td align="right" className="text-text-tertiary whitespace-nowrap !pr-3">
            {row.firstWrittenAt ? formatTime(row.firstWrittenAt) : <Nil />}
          </Td>
        </Tr>
        {isOpen && (
          <tr className="border-b border-border-light bg-surface-secondary/40">
            <td colSpan={cols} className="p-3">
              <SnapshotDay date={row.snapshotDate} />
            </td>
          </tr>
        )}
      </Fragment>
    );
  };

  return (
    <Card padding="sm" className="space-y-4">
      <CardHeader
        title="Snapshot history"
        description={`Nightly per-company archive of the CSM metrics${companiesNote ? ` · ${companiesNote}` : ''}. Open a day for that night’s rows.`}
        action={
          <div className="flex items-center gap-2">
            <InfoTip>
              Captured each night at 02:00 (Brussels) by the “Daily CSM Dashboard Snapshot” automation, per company and
              per person. An empty archive means that automation is not firing: check Automations. Nights that
              stored the same counts are folded into one row. Times are shown as HH:MM in your time zone.{' '}
              <span className="font-medium">Which companies</span>: the job archives every platform company that has a
              domain set in the product, not the CS list. That is why it holds fewer companies than the Customers
              roster: most customers’ platform companies (Xerox, Akkuro, ID Medical…) have no domain, while a few
              internal and test workspaces do. Its token figure is Classic only.
            </InfoTip>
            <DateField label="From" value={from} max={to} onChange={setFrom} />
            <span className="text-body-xs text-text-tertiary">to</span>
            <DateField label="To" value={to} min={from} max={todayISO()} onChange={setTo} />
          </div>
        }
      />

      {/* Said on the page, not only in the hover: every other CSM tab counts the CS list and Classic + aOS. */}
      <p className="rounded-lg border border-border-light bg-surface-secondary px-3 py-2 text-body-sm text-text-secondary">
        <span className="font-medium text-text-primary">Different scope from the rest of CSM.</span>{' '}
        {byDay[0] ? `${formatNumber(byDay[0].companies)} platform companies` : 'Platform companies'} with a domain set in
        the product, not the customers on the CS list, and <span className="font-medium text-text-primary">Classic tokens only</span>{' '}
        (no aOS). Its numbers won&rsquo;t match Customers, Usage or At risk.
      </p>

      {history.isLoading ? (
        <LoadingState />
      ) : history.error ? (
        <ErrorState title="Couldn’t load snapshot history" error={history.error} onRetry={() => history.refetch()} />
      ) : health ? (
        <>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <InlineStat label="First snapshot" value={isoDay(health.firstSnapshotDate) || '·'} size="sm" />
            <InlineStat label="Last snapshot" value={isoDay(health.lastSnapshotDate) || '·'} size="sm" />
            <InlineStat label="Days covered" value={formatNumber(health.daysCovered)} size="sm" />
            <InlineStat
              label="Rows stored"
              value={formatCompact(health.totalCompanyRows) || '0'}
              sub={`${formatNumber(health.totalPersonRows)} person rows`}
              size="sm"
            />
          </div>

          {health.totalCompanyRows === 0 ? (
            <ErrorState
              title="The snapshot archive is empty"
              error="The nightly snapshot job may not be firing. Check the Automations page and the workflow history."
            />
          ) : byDay.length === 0 ? (
            <EmptyState title="No snapshots in this period" compact />
          ) : (
            <>
              <Table colWidths={colWidths}>
                <THead>
                  <tr>
                    <Th />
                    <Th>Snapshot date</Th>
                    {!companiesSame && <Th align="right">Companies</Th>}
                    <Th align="right">Persons</Th>
                    <Th align="right" className="!pr-3" title="Time of day the night's first row was written, your time zone">
                      Written at
                    </Th>
                  </tr>
                </THead>
                <tbody>
                  {runs.map((run) => {
                    if (run.days.length === 1) return dayRow(run.days[0]!);
                    const isOpen = openRuns.has(run.key);
                    const newest = run.days[0]!;
                    const oldest = run.days[run.days.length - 1]!;
                    return (
                      <Fragment key={run.key}>
                        <Tr onClick={() => toggleRun(run.key)} className="bg-surface-secondary">
                          <Td className="pr-2">
                            <ChevronRight
                              className={clsx('h-4 w-4 text-text-muted transition-transform', isOpen && 'rotate-90')}
                              aria-hidden
                            />
                          </Td>
                          <Td>
                            <p className="font-medium text-text-primary">
                              {isoDay(oldest.snapshotDate)} – {isoDay(newest.snapshotDate)}
                            </p>
                            <p className="text-body-xs text-text-tertiary">
                              {run.days.length} nights with the same counts
                            </p>
                          </Td>
                          {!companiesSame && <Td align="right" className="tabular-nums">{formatNumber(newest.companies)}</Td>}
                          <Td align="right" className="tabular-nums">{formatNumber(newest.persons)}</Td>
                          <Td align="right" className="text-text-tertiary whitespace-nowrap !pr-3">
                            {writtenRange(run.days) ?? <Nil />}
                          </Td>
                        </Tr>
                        {isOpen && run.days.map((d) => dayRow(d, true))}
                      </Fragment>
                    );
                  })}
                </tbody>
              </Table>
              <TableFooter>
                {formatNumber(byDay.length)} {byDay.length === 1 ? 'night' : 'nights'} from {isoDay(from)} to {isoDay(to)}
                {runs.length < byDay.length && ` · folded into ${formatNumber(runs.length)} rows`}
              </TableFooter>
            </>
          )}
        </>
      ) : null}
    </Card>
  );
}
